/* eslint-disable @typescript-eslint/no-explicit-any */
import { Session } from './session';
import { FetchHttpClient } from './httpClient/fetchHttpClient';
import { HttpClient, Result } from './httpClient/httpClient';
import { MockHttpClient } from './httpClient/mockHttpClient';
import { ServiceRequest } from './serviceRequest';
import { ServiceResponse } from './serviceResponse';
import { Environment, getDebugService, getDiagnosticsService } from '@mchs/web-core';

enum HttpMethod {
  GET,
  POST,
}

export default abstract class Service<
  TRequest extends ServiceRequest,
  TResponse extends ServiceResponse
> {
  readonly url: string;
  readonly skipTimeoutCheck: boolean;
  readonly debugService = getDebugService();
  readonly diagnosticsService = getDiagnosticsService();

  protected httpCallHandler: HttpClient;

  constructor(service: string, endpoint: string, skipTimeoutCheck = false) {
    this.url = 'https://' + Environment.serviceHostName() + '/' + service + '/' + endpoint;
    this.skipTimeoutCheck = skipTimeoutCheck;

    if (Environment.mockData()) {
      this.httpCallHandler = new MockHttpClient();
    } else {
      this.httpCallHandler = new FetchHttpClient();
    }
  }

  public abstract execute(request?: TRequest): Promise<TResponse>;

  protected abstract handleSuccess(data: any): TResponse;
  protected abstract handleError(message: string): TResponse;

  protected async get(query?: TRequest): Promise<TResponse> {
    return await this.httpCall(HttpMethod.GET, query);
  }

  protected async post(query?: TRequest): Promise<TResponse> {
    return await this.httpCall(HttpMethod.POST, query);
  }

  private async httpCall(
    httpMethod: HttpMethod,
    query?: TRequest
  ): Promise<TResponse> {
    let serviceLoggingPreface = '';
    if (Environment.mockData()) {
      serviceLoggingPreface = 'MOCK ';
    }

    this.debugService.log(
      `${serviceLoggingPreface}Service`,
      `Service Execution STARTED (${this.url})`,
      query
    );

    try {
      let httpCallResult: Result;

      if (httpMethod === HttpMethod.GET) {
        httpCallResult = await this.httpCallHandler.get(
          this.url,
          Session.Instance.token,
          query
        );
      } else {
        httpCallResult = await this.httpCallHandler.post(
          this.url,
          Session.Instance.token,
          query
        );
      }

      let response: TResponse;

      if (httpCallResult.succeeded()) {
        response = this.handleSuccess(httpCallResult.response);
        response.succeeded = true;
        response.authorized = true;
        this.debugService.log(
          `${serviceLoggingPreface}Service`,
          `Service Execution SUCCEEDED (${this.url})`,
          response
        );
      } else {
        response = this.handleError(httpCallResult.response.data);
        response.succeeded = false;
        response.authorized = httpCallResult.authorized();
        if (!response.authorized && !this.skipTimeoutCheck) {
          Session.Instance.sessionTimeout();
        }
        this.debugService.log(
          `${serviceLoggingPreface}Service`,
          `Service Execution FAILED ${this.url}`,
          response
        );
      }
      return response;
    } catch (error: any) {
      // Log this error to the diagnostics service for further analysis.
      this.diagnosticsService.logError(
        'Service',
        'An unhandled exception was caught. ' + error.message
      );
      return this.handleError(error.message);
    }
  }
}
